import React, { useEffect, useState } from "react";
import {
  Select,
  Spinner,
  Box,
  Text,
  Image,
  Menu,
  MenuButton,
  Icon,
  MenuList,
  MenuItem,
  Flex,
  IconButton,
  CloseButton,
  useToast, // Import useToast
} from "@chakra-ui/react";
import { FiMoreVertical } from "react-icons/fi";
import axios from "axios";
import { BiTrash } from "react-icons/bi"; // Import the remove bin icon
import { BASE_URL } from "../../Globalapi/Globalapi";

const StudentDropdown = () => {
  const [houses, setHouses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedHouse, setSelectedHouse] = useState("");
  const [houseLeadership, setHouseLeadership] = useState({});
  const toast = useToast(); // Initialize the toast

  useEffect(() => {
    const fetchHouses = async () => {
      try {
        const response = await axios.get(
          `${BASE_URL}/admin/house/list_all_house.php`
        );
        console.log("Houses Response:", response.data);

        if (response.data.success) {
          setHouses(response.data.events);
          await fetchHouseLeadership(); // Fetch leadership data after houses are loaded
        } else {
          toast({
            title: "Error",
            description: response.data.message || "Failed to load houses",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error fetching houses:", error);
        toast({
          title: "Error",
          description: "Failed to load houses",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    const fetchHouseLeadership = async () => {
      try {
        const leadershipResponse = await axios.get(
          `${BASE_URL}/admin/house/list_all_house_captain.php`
        );

        console.log("Leadership Response:", leadershipResponse.data); // Log the entire response for debugging

        const leadershipData = {};

        if (leadershipResponse.data.success) {
          const captains = leadershipResponse.data.captains || []; // Ensure captains is an array
          captains.forEach((captain) => {
            // Ensure the house_id is present in the captain object
            if (!captain.house_id) {
              console.warn("Missing house_id for captain:", captain);
              return; // Skip if house_id is missing
            }

            // Initialize the house leadership entry if it doesn't exist
            if (!leadershipData[captain.house_id]) {
              leadershipData[captain.house_id] = {
                captain: null,
                viceCaptain: null,
              };
            }

            // Assign Captain or Vice-Captain based on the role
            if (captain.captain_role === "Captain") {
              leadershipData[captain.house_id].captain = captain;
            } else if (captain.captain_role === "Vice Captain") {
              leadershipData[captain.house_id].viceCaptain = captain;
            }
          });

          console.log("Processed Leadership Data:", leadershipData); // Debugging line after processing
          setHouseLeadership(leadershipData);
        } else {
          toast({
            title: "Error",
            description:
              leadershipResponse.data.message ||
              "Failed to load house leadership",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error fetching leadership:", error);
        toast({
          title: "Error",
          description: "Failed to load house leadership",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    fetchHouses();
  }, []);

  const fetchHouseStudents = async (houseId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${BASE_URL}/admin/house/list_house_students.php`,
        {
          house_id: houseId,
        }
      );

      if (response.data.success) {
        setStudents(response.data.data);
      } else {
        setStudents([]);
        toast({
          title: "Error",
          description: response.data.message || "Failed to load students",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error fetching students:", error);
      toast({
        title: "Error",
        description: "Failed to load students",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleHouseSelect = (e) => {
    const selectedHouseId = e.target.value;
    setSelectedHouse(selectedHouseId);

    if (selectedHouseId) {
      fetchHouseStudents(selectedHouseId);
    } else {
      setStudents([]);
    }
  };

  const handleSelectCaptain = async (student) => {
    if (selectedHouse) {
      const leadership = houseLeadership[selectedHouse];
      if (leadership?.captain) {
        toast({
          title: "Warning",
          description: `Captain already assigned: ${leadership.captain.student_name}`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      try {
        const response = await axios.post(
          `${BASE_URL}/admin/house/create_house_captain.php`,
          {
            house_id: selectedHouse,
            student_id: student.student_id,
            captain_master_id: 1, // Assuming 1 is for Captain
          }
        );

        if (response.data.success) {
          setHouseLeadership((prev) => ({
            ...prev,
            [selectedHouse]: {
              ...prev[selectedHouse],
              captain: student,
            },
          }));
          toast({
            title: "Success",
            description: `${student.student_name} selected as Captain`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: "Error",
            description: response.data.message || "Failed to select Captain",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error assigning Captain:", error);
        toast({
          title: "Error",
          description: "Failed to assign Captain",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } else {
      toast({
        title: "Warning",
        description: "Please select a house first",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSelectViceCaptain = async (student) => {
    if (selectedHouse) {
      const leadership = houseLeadership[selectedHouse];
      if (leadership?.viceCaptain) {
        toast({
          title: "Warning",
          description: `Vice-Captain already assigned: ${leadership.viceCaptain.student_name}`,
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      try {
        const response = await axios.post(
          `${BASE_URL}/admin/house/create_house_captain.php`,
          {
            house_id: selectedHouse,
            student_id: student.student_id,
            captain_master_id: 2, // Assuming 2 is for Vice-Captain
          }
        );

        if (response.data.success) {
          // Update the state with the new Vice-Captain
          setHouseLeadership((prev) => ({
            ...prev,
            [selectedHouse]: {
              ...prev[selectedHouse],
              viceCaptain: student, // Ensure this is set correctly
            },
          }));
          console.log(
            "Updated house leadership after selecting Vice-Captain:",
            {
              ...houseLeadership,
              [selectedHouse]: {
                ...houseLeadership[selectedHouse],
                viceCaptain: student,
              },
            }
          ); // Debugging line
          toast({
            title: "Success",
            description: `${student.student_name} selected as Vice-Captain`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: "Error",
            description:
              response.data.message || "Failed to select Vice-Captain",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error assigning Vice-Captain:", error);
        toast({
          title: "Error",
          description: "Failed to assign Vice-Captain",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } else {
      toast({
        title: "Warning",
        description: "Please select a house first",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteLeadership = async (role) => {
    if (selectedHouse) {
      const leadership = houseLeadership[selectedHouse];
      let captainId; // Variable to hold the captain's ID

      // Determine which ID to use based on the role
      if (role === "Captain" && leadership?.captain) {
        captainId = leadership.captain.id; // Get captain ID
      } else if (role === "Vice Captain" && leadership?.viceCaptain) {
        captainId = leadership.viceCaptain.id; // Get vice-captain ID
      } else {
        toast({
          title: "Error",
          description: `No ${role} assigned to remove.`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      try {
        const response = await axios.delete(
          `${BASE_URL}/admin/house/delete_house_captain.php`,
          {
            data: { house_captain_id: captainId }, // Send the correct captain ID in the body
          }
        );

        if (response.data.success) {
          setHouseLeadership((prev) => ({
            ...prev,
            [selectedHouse]: {
              ...prev[selectedHouse],
              [role.toLowerCase()]: null, // Remove the leadership role
            },
          }));
          toast({
            title: "Success",
            description: `${role} removed successfully`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: "Error",
            description: response.data.message || `Failed to remove ${role}`,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error removing leadership:", error);
        toast({
          title: "Error",
          description: "Failed to remove leadership",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } else {
      toast({
        title: "Warning",
        description: "Please select a house first",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Box>


      
      {/* House Dropdown */}
      <Flex justify="flex-end" mt={4} width="100%">
  <Select
    width="250px"
    placeholder="Select a house"
    onChange={handleHouseSelect}
    bg="gray.50"
    borderColor="blue.500"
    _hover={{ borderColor: "blue.300" }}
    _focus={{ borderColor: "blue.700", boxShadow: "outline" }}
  >
    {houses.map((house) => (
      <option
        key={house.id}
        value={house.id}
        style={{
          backgroundColor: house.color || "white",
          color: house.textColor || "black",
        }}
      >
        {house.caption}
      </option>
    ))}
  </Select>
</Flex>









      {/* Display Leadership for Selected House */}
      {selectedHouse && (
  <Box mt={4} p={4} bg="blue.50" borderRadius="md">
    <Text fontWeight="bold" color="blue.800" fontSize="xl">
      {houses.find((h) => h.house_id === selectedHouse)?.house_name} Leadership
    </Text>

    {/* Leadership Section - Captain and Vice-Captain in the same row */}
    <Box display="flex" justifyContent="space-between" mt={4}>
      {/* Captain Section */}
      <Box flex="1" mr={2}>
        <Text fontWeight="bold" color="blue.600">Captain:</Text>
        {houseLeadership[selectedHouse]?.captain ? (
          <Box
            display="flex"
            alignItems="center"
            mt={1}
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center">
              <Image
                src={
                  houseLeadership[selectedHouse].captain.thumb_img ||
                  "https://t4.ftcdn.net/jpg/02/29/75/83/360_F_229758328_7x8jwCwjtBMmC6rgFzLFhZoEpLobB6L8.jpg"
                }
                borderRadius="full"
                mr={2}
                boxSize="2rem"
                onError={(e) => {
                  e.target.src =
                    "https://t4.ftcdn.net/jpg/02/29/75/83/360_F_229758328_7x8jwCwjtBMmC6rgFzLFhZoEpLobB6L8.jpg";
                }}
              />
              <Text fontWeight="bold" color="blue.800">
                {houseLeadership[selectedHouse].captain.student_name}
              </Text>
            </Box>
            <Icon
              as={BiTrash} // Use the remove bin icon
              boxSize={6} // Set the size of the icon
              color="red.500" // Set the icon color to red
              ml={4} // Adjust margin-left for spacing
              cursor="pointer" // Change cursor to pointer for better UX
              onClick={() => handleDeleteLeadership("Captain")} // Handle click event
            />
          </Box>
        ) : (
          <Text color="gray.500">No Captain assigned</Text>
        )}
      </Box>

      {/* Vice-Captain Section */}
      <Box flex="1">
        <Text fontWeight="bold" color="blue.600">Vice-Captain:</Text>
        {houseLeadership[selectedHouse]?.viceCaptain ? (
          <Box
            display="flex"
            alignItems="center"
            mt={1}
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center">
              <Image
                src={
                  houseLeadership[selectedHouse].viceCaptain.thumb_img ||
                  "https://t4.ftcdn.net/jpg/02/29/75/83/360_F_229758328_7x8jwCwjtBMmC6rgFzLFhZoEpLobB6L8.jpg"
                }
                borderRadius="full"
                mr={2}
                boxSize="2rem"
                onError={(e) => {
                  e.target.src =
                    "https://t4.ftcdn.net/jpg/02/29/75/83/360_F_229758328_7x8jwCwjtBMmC6rgFzLFhZoEpLobB6L8.jpg";
                }}
              />
              <Text fontWeight="bold" color="blue.800">
                {houseLeadership[selectedHouse].viceCaptain.student_name}
              </Text>
            </Box>
            <Icon
              as={BiTrash} // Use the remove bin icon
              boxSize={6} // Set the size of the icon
              color="red.500" // Set the icon color to red
              ml={4} // Adjust margin-left for spacing
              cursor="pointer" // Change cursor to pointer for better UX
              onClick={() => handleDeleteLeadership("Vice Captain")} // Handle click event
            />
          </Box>
        ) : (
          <Text color="gray.500">No Vice-Captain assigned</Text>
        )}
      </Box>
    </Box>
  </Box>
)}

{/* Loading Spinner */}
{loading && (
  <Box textAlign="center" my={4}>
    <Spinner size="md" color="blue.500" />
  </Box>
)}

{/* Error Message */}
{error && (
  <Box textAlign="center" my={4}>
    <Text color="red.500">{error}</Text>
  </Box>
)}

{/* Display List of Students */}
{!loading && students.length > 0 && (
  <Box
    my={4}
    maxWidth="100%"
    borderWidth="1px"
    borderRadius="md"
    overflow="hidden"
    bg="white"
    boxShadow="lg"
  >
    <Box
      display="grid"
      gridTemplateColumns={{
        base: "repeat(1, 1fr)",
        md: "repeat(3, 1fr)",
        lg: "repeat(4, 1fr)",
      }}
      gap={4}
      p={4}
    >
      {students.map((student) => (
        <Box
          key={student.student_id}
          display="flex"
          alignItems="center"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          p={4}
          backgroundColor="white"
          boxShadow="md"
          _hover={{ bg: "blue.50", cursor: "pointer" }}
        >
          <Image
            src={
              student.image_url ||
              "https://t4.ftcdn.net/jpg/02/29/75/83/360_F_229758328_7x8jwCwjtBMmC6rgFzLFhZoEpLobB6L8.jpg"
            }
            borderRadius="full"
            mr={2}
            boxSize="2rem"
            onError={(e) => {
              e.target.src =
                "https://t4.ftcdn.net/jpg/02/29/75/83/360_F_229758328_7x8jwCwjtBMmC6rgFzLFhZoEpLobB6L8.jpg";
            }}
          />
          <Text
            fontSize="md"
            fontWeight="bold"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            flex="1"
            color="blue.800"
          >
            {student.student_name}
          </Text>

          {/* 3 Dots Menu */}
          <Menu>
            <MenuButton as={IconButton} icon={<FiMoreVertical />} />
            <MenuList>
              <MenuItem onClick={() => handleSelectCaptain(student)}>
                Select as Captain
              </MenuItem>
              <MenuItem onClick={() => handleSelectViceCaptain(student)}>
                Select as Vice-Captain
              </MenuItem>
            </MenuList>
          </Menu>
        </Box>
      ))}
    </Box>
  </Box>
)}

    </Box>
  );
};

export default StudentDropdown;
